import axios from "axios";
import { API } from "./apiClient";

const adminGraphql = async (query, variables = {}) => {
  const response = await axios.post(API, { query, variables }, { withCredentials: true });
  const failure = response.data?.errors?.[0];
  if (failure) {
    const error = new Error(failure.message || "Admin request failed");
    error.code = failure.extensions?.code;
    throw error;
  }
  return response.data?.data;
};

const QUEUE_ITEM = `source_type source_id target_type target_id target_label status submitted_at has_contact has_submitter summary`;
const QUEUE_DETAIL = `${QUEUE_ITEM} submitter_user_id contact_name contact_email context_json`;
const FEATURED = `destination_id featured featured_order featured_at featured_by_user_id`;
const PROVENANCE = `field source_type source_url observed_at verified_at verified_by_user_id`;

export const adminApi = {
  async listReviewQueue({ sourceTypes, statuses, limit = 50 } = {}) {
    const data = await adminGraphql(`query AdminReviewQueue($sourceTypes: [AdminReviewQueueSourceType!], $statuses: [AdminReviewQueueStatus!], $limit: Int) {
      adminReviewQueue(source_types: $sourceTypes, statuses: $statuses, limit: $limit) { ${QUEUE_ITEM} }
    }`, { sourceTypes: sourceTypes?.length ? sourceTypes : null, statuses: statuses?.length ? statuses : null, limit });
    return data.adminReviewQueue;
  },

  async getReviewQueueItem(sourceType, sourceId) {
    const data = await adminGraphql(`query AdminReviewQueueItem($sourceType: AdminReviewQueueSourceType!, $sourceId: ID!) {
      adminReviewQueueItem(source_type: $sourceType, source_id: $sourceId) { ${QUEUE_DETAIL} }
    }`, { sourceType, sourceId });
    return data.adminReviewQueueItem;
  },

  async resolveQueueItem(payload) {
    if (payload.source_type === "SUGGEST_EDIT") {
      const data = await adminGraphql(`mutation ResolveSuggestEdit($input: ResolveSuggestEditProposalInput!) {
        resolveSuggestEditProposal(input: $input) { proposal_id status target_type target_id reviewed_at reviewed_by_user_id changed_fields }
      }`, {
        input: {
          proposal_id: payload.source_id,
          decision: payload.decision,
          resolution_note: payload.resolution_note,
          edited_changes: payload.edited_changes ?? null,
        },
      });
      return data.resolveSuggestEditProposal;
    }
    if (payload.source_type === "ADD_DESTINATION") {
      const data = await adminGraphql(`mutation ResolveDestinationProposal($input: ResolveDestinationProposalInput!) {
        resolveDestinationProposal(input: $input) { proposal_id status reviewed_at reviewed_by_user_id destination_id destination_slug }
      }`, { input: { proposal_id: payload.source_id, decision: payload.decision, resolution_note: payload.resolution_note } });
      return data.resolveDestinationProposal;
    }
    if (payload.source_type === "ADD_TRAIL") {
      const data = await adminGraphql(`mutation ResolveTrailProposal($input: ResolveTrailProposalInput!) {
        resolveTrailProposal(input: $input) { proposal_id status reviewed_at reviewed_by_user_id destination_id trail_id trail_slug }
      }`, { input: { proposal_id: payload.source_id, decision: payload.decision, resolution_note: payload.resolution_note } });
      return data.resolveTrailProposal;
    }
    if (payload.source_type === "REPORT") {
      const data = await adminGraphql(`mutation ResolveReviewCase($input: ResolveReviewCaseInput!) {
        resolveReviewCase(input: $input) { review_case_id status target_type target_id action reviewed_at reviewed_by_user_id }
      }`, { input: { review_case_id: payload.source_id, action: payload.action, resolution_note: payload.resolution_note } });
      return data.resolveReviewCase;
    }
    if (payload.source_type === "STATUS_CHANGE") {
      const data = await adminGraphql(`mutation ResolveStatusChange($input: ResolveStatusChangeInput!) {
        resolveStatusChange(input: $input) { candidate_id status destination_slug reviewed_at reviewed_by_user_id }
      }`, {
        input: {
          candidate_id: payload.source_id,
          decision: payload.decision,
          resolution_note: payload.resolution_note,
        },
      });
      return data.resolveStatusChange;
    }
    throw new Error(`Unsupported review queue source '${payload.source_type}'`);
  },

  async createDestination(input) {
    const data = await adminGraphql(`mutation AdminCreateDestination($input: AdminCreateDestinationInput!) {
      adminCreateDestination(input: $input) { entity_type entity_id changed_fields provenance { ${PROVENANCE} } }
    }`, { input });
    return data.adminCreateDestination;
  },

  async createTrail(input) {
    const data = await adminGraphql(`mutation AdminCreateTrail($input: AdminCreateTrailInput!) {
      adminCreateTrail(input: $input) { entity_type entity_id changed_fields provenance { ${PROVENANCE} } }
    }`, { input });
    return data.adminCreateTrail;
  },

  async updateCanonical(entityType, input) {
    const operation = entityType === "TRAIL" ? "adminUpdateTrail" : "adminUpdateDestination";
    const data = await adminGraphql(`mutation AdminUpdateCanonical($input: AdminUpdateCanonicalInput!) {
      ${operation}(input: $input) { entity_type entity_id changed_fields provenance { ${PROVENANCE} } }
    }`, { input });
    return data[operation];
  },

  async getCanonicalProvenance(entityType, entityId) {
    const data = await adminGraphql(`query AdminCanonicalProvenance($entityType: AdminCanonicalEntityType!, $entityId: ID!) {
      adminCanonicalProvenance(entity_type: $entityType, entity_id: $entityId) { ${PROVENANCE} }
    }`, { entityType, entityId });
    return data.adminCanonicalProvenance;
  },

  async listFeatured() {
    const data = await adminGraphql(`query AdminFeaturedDestinations { adminFeaturedDestinations { ${FEATURED} } }`);
    return data.adminFeaturedDestinations;
  },

  async setFeatured(destinationId, reason) {
    const data = await adminGraphql(`mutation AdminSetFeatured($destinationId: ID!, $reason: String!) {
      adminSetFeatured(destination_id: $destinationId, reason: $reason) { destinations { ${FEATURED} } }
    }`, { destinationId, reason });
    return data.adminSetFeatured.destinations;
  },

  async clearFeatured(destinationId, reason) {
    const data = await adminGraphql(`mutation AdminClearFeatured($destinationId: ID!, $reason: String!) {
      adminClearFeatured(destination_id: $destinationId, reason: $reason) { destinations { ${FEATURED} } }
    }`, { destinationId, reason });
    return data.adminClearFeatured.destinations;
  },

  async reorderFeatured(destinationIds, reason) {
    const data = await adminGraphql(`mutation AdminReorderFeatured($destinationIds: [ID!]!, $reason: String!) {
      adminReorderFeatured(destination_ids: $destinationIds, reason: $reason) { destinations { ${FEATURED} } }
    }`, { destinationIds, reason });
    return data.adminReorderFeatured.destinations;
  },

  async listAudit(limit = 50) {
    const data = await adminGraphql(`query AdminAuditEvents($limit: Int) {
      adminAuditEvents(limit: $limit) { id actor_user_id actor_role action target_type target_id context_json before_json after_json occurred_at }
    }`, { limit });
    return data.adminAuditEvents;
  },
};
